import { SearchIcon } from '@chakra-ui/icons';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Flex,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  Text,
  useColorModeValue,
  VStack,
} from '@chakra-ui/react';
import Card from 'components/card/Card';

/**
 * Sidebar navigation for help documentation
 */
const HelpSidebar = ({
  pageGuides,
  lexicon,
  activeSection,
  navigateToSection,
  searchQuery,
  setSearchQuery,
}) => {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const activeColor = useColorModeValue('brand.500', 'brand.400');
  const hoverBg = useColorModeValue('gray.100', 'whiteAlpha.100');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const inputBg = useColorModeValue('white', 'gray.800');

  const handleSectionClick = (sectionId) => {
    navigateToSection(sectionId);
  };

  return (
    <Card
      p="20px"
      position="sticky"
      top="130px"
      maxH="calc(100vh - 160px)"
      overflowY="auto"
    >
      <VStack spacing="4" align="stretch">
        {/* Search Box */}
        <Box>
          <InputGroup size="md">
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="secondaryGray.600" />
            </InputLeftElement>
            <Input
              placeholder="Search help..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              bg={inputBg}
              borderColor={borderColor}
              pl="10"
              _focus={{
                borderColor: activeColor,
                boxShadow: `0 0 0 1px ${activeColor}`,
              }}
            />
          </InputGroup>
        </Box>

        {/* Navigation Sections */}
        <Accordion allowMultiple defaultIndex={[0, 1]}>
          {/* Page Guides Section */}
          <AccordionItem border="none">
            <AccordionButton px="0" _hover={{ bg: 'transparent' }}>
              <Box flex="1" textAlign="left">
                <Heading as="h3" size="sm" color={textColor}>
                  Page Guides
                </Heading>
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel px="0" pb="4">
              <VStack spacing="1" align="stretch">
                {pageGuides.map((guide) => (
                  <Box key={guide.id}>
                    <Text
                      fontSize="sm"
                      fontWeight="600"
                      color={textColor}
                      mb="1"
                      mt="3"
                    >
                      {guide.title}
                    </Text>
                    <VStack spacing="0.5" align="stretch" pl="3">
                      {guide.sections.map((section) => {
                        const sectionId = `${guide.id}-${section.id}`;
                        const isActive = activeSection === sectionId;
                        return (
                          <Flex
                            key={sectionId}
                            px="3"
                            py="2"
                            borderRadius="md"
                            cursor="pointer"
                            bg={isActive ? hoverBg : 'transparent'}
                            color={isActive ? activeColor : textColor}
                            _hover={{ bg: hoverBg }}
                            onClick={() => handleSectionClick(sectionId)}
                            fontSize="sm"
                            align="center"
                            borderLeftWidth="2px"
                            borderLeftColor={
                              isActive ? activeColor : 'transparent'
                            }
                          >
                            <Text>{section.title}</Text>
                          </Flex>
                        );
                      })}
                    </VStack>
                  </Box>
                ))}
              </VStack>
            </AccordionPanel>
          </AccordionItem>

          {/* Lexicon Section */}
          <AccordionItem border="none">
            <AccordionButton px="0" _hover={{ bg: 'transparent' }}>
              <Box flex="1" textAlign="left">
                <Heading as="h3" size="sm" color={textColor}>
                  Lexicon
                </Heading>
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel px="0" pb="4">
              <VStack spacing="0.5" align="stretch">
                {lexicon
                  .slice()
                  .sort((a, b) => a.term.localeCompare(b.term))
                  .map((term) => {
                    const isActive = activeSection === term.id;
                    return (
                      <Flex
                        key={term.id}
                        px="3"
                        py="2"
                        borderRadius="md"
                        cursor="pointer"
                        bg={isActive ? hoverBg : 'transparent'}
                        color={isActive ? activeColor : textColor}
                        _hover={{ bg: hoverBg }}
                        onClick={() => handleSectionClick(term.id)}
                        fontSize="sm"
                        align="center"
                        justify="space-between"
                        borderLeftWidth="2px"
                        borderLeftColor={
                          isActive ? activeColor : 'transparent'
                        }
                      >
                        <Text fontWeight={isActive ? '600' : '400'}>
                          {term.term}
                        </Text>
                        <Text
                          fontSize="xs"
                          color="secondaryGray.600"
                          ml="2"
                          noOfLines={1}
                        >
                          {term.fullTerm}
                        </Text>
                      </Flex>
                    );
                  })}
              </VStack>
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      </VStack>
    </Card>
  );
};

export default HelpSidebar;
